import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, NavLink as RouterNavLink, useSearchParams } from 'react-router-dom';
import { Image } from 'semantic-ui-react';
import { Menu } from '../../layout/Menu';
import { device } from '../../../theme';

// Import person statistics data
let personStatisticsData = {};
try {
  personStatisticsData = require('../../../data/person-statistics.json');
} catch (e) {
  console.warn('Person statistics data not yet generated. Run: npm run generate-person-statistics');
}

export const PersonStatistics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [selectedYears, setSelectedYears] = useState(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('nsffPoints');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const yearDropdownRef = React.useRef(null);

  const competitions = useMemo(() => {
    return Object.keys(personStatisticsData)
      .filter(key => Object.keys(personStatisticsData[key]).length > 0)
      .sort((a, b) => a.localeCompare(b, 'nb')); // Sort alphabetically with Norwegian locale
  }, []);

  const years = useMemo(() => {
    if (!selectedCompetition || !personStatisticsData[selectedCompetition]) {
      return [];
    }
    return Object.keys(personStatisticsData[selectedCompetition]).sort((a, b) => {
      if (a === 'all') return 1;
      if (b === 'all') return -1;

      // Extract year numbers for sorting (handles both "2014" and "2014 Høst" formats)
      const yearA = parseInt(a);
      const yearB = parseInt(b);

      // If years are different, sort by year (newest first)
      if (yearA !== yearB) {
        return yearB - yearA;
      }

      // If same year, sort by part (Vår before Høst)
      return a.localeCompare(b, 'nb');
    });
  }, [selectedCompetition]);

  const personData = useMemo(() => {
    if (!selectedCompetition || selectedYears.length === 0 || !personStatisticsData[selectedCompetition]) {
      return [];
    }

    // If 'all' is selected, calculate participation counts from individual years
    if (selectedYears.includes('all')) {
      const allYearsData = personStatisticsData[selectedCompetition]['all'] || [];

      // Calculate participation count for each person, counting unique years only
      const participationMap = {};
      const allAvailableYears = Object.keys(personStatisticsData[selectedCompetition])
        .filter(year => year !== 'all');

      allAvailableYears.forEach(yearKey => {
        const yearData = personStatisticsData[selectedCompetition][yearKey] || [];
        // Extract the year number from strings like "2024" or "2024 Høst"
        const yearNumber = parseInt(yearKey);

        yearData.forEach(person => {
          const personKey = person.personId;

          // Track unique years per person
          if (!participationMap[personKey]) {
            participationMap[personKey] = new Set();
          }
          participationMap[personKey].add(yearNumber);
        });
      });

      // Add participation count to each person (count of unique years)
      return allYearsData.map(person => ({
        ...person,
        participationCount: participationMap[person.personId]?.size || 0
      }));
    }

    // Aggregate data from multiple selected years
    const aggregatedData = {};
    const participationYears = {}; // Track unique years per person

    selectedYears.forEach(yearKey => {
      const yearData = personStatisticsData[selectedCompetition]?.[yearKey];
      if (!yearData) return;

      // Extract the year number from strings like "2024" or "2024 Høst"
      const yearNumber = parseInt(yearKey);

      yearData.forEach(person => {
        const key = person.personId;

        if (!aggregatedData[key]) {
          aggregatedData[key] = {
            personId: person.personId,
            personName: person.personName,
            clubName: person.clubName,
            nsffPoints: 0,
            totalAwards: 0,
            goldCount: 0,
            silverCount: 0,
            bronzeCount: 0,
            hmCount: 0,
            acceptedCount: 0
          };
          participationYears[key] = new Set();
        }

        // Sum up the statistics
        aggregatedData[key].nsffPoints += person.nsffPoints || 0;
        aggregatedData[key].totalAwards += person.totalAwards || 0;
        aggregatedData[key].goldCount += person.goldCount || 0;
        aggregatedData[key].silverCount += person.silverCount || 0;
        aggregatedData[key].bronzeCount += person.bronzeCount || 0;
        aggregatedData[key].hmCount += person.hmCount || 0;
        aggregatedData[key].acceptedCount += person.acceptedCount || 0;

        // Track unique years
        participationYears[key].add(yearNumber);
      });
    });

    // Add participation count based on unique years
    return Object.values(aggregatedData).map(person => ({
      ...person,
      participationCount: participationYears[person.personId]?.size || 0
    }));
  }, [selectedCompetition, selectedYears]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return personData;
    const term = searchTerm.toLowerCase();
    return personData.filter(person =>
      person.personName.toLowerCase().includes(term) ||
      person.clubName.toLowerCase().includes(term)
    );
  }, [personData, searchTerm]);

  const sortedData = useMemo(() => {
    console.log('Sorting by:', sortColumn, 'direction:', sortDirection);
    const sorted = [...personData];
    sorted.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle undefined/null values
      if (aVal === undefined || aVal === null) aVal = 0;
      if (bVal === undefined || bVal === null) bVal = 0;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        if (sortDirection === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      }

      // Numeric comparison
      if (sortDirection === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });
    console.log('First 10 after sort:', sorted.slice(0, 10).map(p => `${p.personName}: ${p[sortColumn]}`));
    return sorted;
  }, [personData, sortColumn, sortDirection]);

  const filteredDataWithRanks = useMemo(() => {
    const term = searchTerm.toLowerCase();

    // Assign ranks based on sort column value (handle ties)
    const dataWithRanks = [];
    let currentRank = 1;
    let previousValue = null;
    let sameRankCount = 0;

    sortedData.forEach((person, index) => {
      const currentValue = person[sortColumn];

      // If value changed, update rank
      if (index > 0 && currentValue !== previousValue) {
        currentRank = index + 1;
      }

      dataWithRanks.push({
        ...person,
        rank: currentRank
      });

      previousValue = currentValue;
    });

    // Filter after ranking
    return dataWithRanks.filter(person =>
      !searchTerm ||
      person.personName.toLowerCase().includes(term) ||
      person.clubName.toLowerCase().includes(term)
    );
  }, [sortedData, searchTerm, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Initialize from URL or set default
  useEffect(() => {
    const urlComp = searchParams.get('competition');
    if (urlComp && competitions.includes(urlComp)) {
      setSelectedCompetition(urlComp);
    } else if (!selectedCompetition && competitions.length > 0) {
      // Prefer 'NM' if available, otherwise use first competition
      const defaultComp = competitions.includes('NM') ? 'NM' : competitions[0];
      setSelectedCompetition(defaultComp);
      setSearchParams({ competition: defaultComp });
    }
  }, [competitions]);

  // Update URL when competition changes
  const handleCompetitionChange = (comp) => {
    setSelectedCompetition(comp);
    setSearchParams({ competition: comp });
  };

  React.useEffect(() => {
    if (years.length > 0 && !selectedYears.some(year => years.includes(year))) {
      setSelectedYears(['all']);
    }
  }, [years, selectedYears]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setIsYearDropdownOpen(false);
      }
    };

    if (isYearDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isYearDropdownOpen]);

  const handleYearToggle = (year) => {
    if (year === 'all') {
      setSelectedYears(['all']);
    } else {
      const newSelection = selectedYears.includes('all')
        ? [year]
        : selectedYears.includes(year)
          ? selectedYears.filter(y => y !== year)
          : [...selectedYears, year];

      if (newSelection.length === 0) {
        setSelectedYears(['all']);
      } else {
        setSelectedYears(newSelection);
      }
    }
  };

  const getYearDisplayText = () => {
    if (selectedYears.includes('all')) {
      return 'Alle år';
    }
    if (selectedYears.length === 1) {
      return selectedYears[0];
    }
    return `${selectedYears.length} år valgt`;
  };

  return (
    <>
      <Header>
        <div>
          <a href="https://nsff.no">
            <Image src={"/resultater/images/logo-darkgreen.png"} alt="Logo" height={"60"} />
          </a>
        </div>
        <div>
          <h1>Personstatistikk</h1>
        </div>
        <div>
          <Link to={"/"}>Tilbake</Link>
        </div>
      </Header>
      <Menu />
      <Container>
        <ContentWrapper>
          <Title>Personstatistikk</Title>

          <NavLinks>
            <NavLink to={`/statistikk${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`} end>Konkurranser</NavLink>
            <NavLink to={`/statistikk/personer${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`}>Personer</NavLink>
            <NavLink to={`/statistikk/klubber${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`}>Klubber</NavLink>
            <NavLink to="/statistikk/hederstegn">Hederstegn</NavLink>
            <NavLink to="/statistikk/nsff">NSFF</NavLink>
          </NavLinks>

          <FilterRow>
            <FilterGroup>
              <label htmlFor="competition-select">Konkurranse: </label>
              <select
                id="competition-select"
                value={selectedCompetition}
                onChange={(e) => handleCompetitionChange(e.target.value)}
              >
                {competitions.map(comp => (
                  <option key={comp} value={comp}>{comp}</option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup ref={yearDropdownRef}>
              <label>År: </label>
              <YearDropdownContainer>
                <YearDropdownButton onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}>
                  {getYearDisplayText()}
                  <DropdownArrow>{isYearDropdownOpen ? '▲' : '▼'}</DropdownArrow>
                </YearDropdownButton>
                {isYearDropdownOpen && (
                  <YearDropdownList>
                    {years.map(year => (
                      <YearCheckboxItem key={year}>
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedYears.includes(year)}
                            onChange={() => handleYearToggle(year)}
                          />
                          <span>{year === 'all' ? 'Alle år' : year}</span>
                        </label>
                      </YearCheckboxItem>
                    ))}
                  </YearDropdownList>
                )}
              </YearDropdownContainer>
            </FilterGroup>

            <FilterGroup>
              <label htmlFor="search">Søk: </label>
              <SearchInput
                id="search"
                type="text"
                placeholder="Søk etter navn eller klubb..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </FilterGroup>
          </FilterRow>

          {filteredDataWithRanks.length > 0 && (
            <>
              <ResultCount>Viser {filteredDataWithRanks.length} {searchTerm && `av ${sortedData.length}`} deltakere</ResultCount>
              <TableWrapper>
                <StatsTable>
                  <thead>
                    <tr>
                      <th>#</th>
                      <SortableHeader onClick={() => handleSort('personName')}>
                        Navn {sortColumn === 'personName' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('clubName')}>
                        Klubb {sortColumn === 'clubName' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('nsffPoints')}>
                        NSFF poeng {sortColumn === 'nsffPoints' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('participationCount')} title="Antall år personen har deltatt">
                        År {sortColumn === 'participationCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('totalAwards')}>
                        Sum {sortColumn === 'totalAwards' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('goldCount')}>
                        Gull {sortColumn === 'goldCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('silverCount')}>
                        Sølv {sortColumn === 'silverCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('bronzeCount')}>
                        Bronse {sortColumn === 'bronzeCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('hmCount')}>
                        HM {sortColumn === 'hmCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('acceptedCount')}>
                        Antatt {sortColumn === 'acceptedCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDataWithRanks.map((person, idx) => (
                      <tr key={`${person.personName}-${idx}`}>
                        <RankCell>{person.rank}</RankCell>
                        <td>{person.personName}</td>
                        <td>{person.clubName}</td>
                        <td>{person.nsffPoints || 0}</td>
                        <td>{person.participationCount || 0}</td>
                        <td>{person.totalAwards}</td>
                        <td>{person.goldCount || '-'}</td>
                        <td>{person.silverCount || '-'}</td>
                        <td>{person.bronzeCount || '-'}</td>
                        <td>{person.hmCount || '-'}</td>
                        <td>{person.acceptedCount || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </StatsTable>
              </TableWrapper>
            </>
          )}

          {filteredDataWithRanks.length === 0 && personData.length === 0 && (
            <NoData>Ingen statistikk tilgjengelig for dette valget</NoData>
          )}

          {filteredDataWithRanks.length === 0 && personData.length > 0 && (
            <NoData>Ingen treff for søket "{searchTerm}"</NoData>
          )}
        </ContentWrapper>
      </Container>
    </>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  height: 70px;
  background-color: #90b361;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #36501c;
  font-family: 'Fibon Neue';

  > div {
    display: flex;
    flex-direction: row;
    margin-left: 10px;

    > div {
      margin-left: 20px;
    }

    > a, > h1 {
      color: ${(p) => p.theme.color.primary.base};
    }

    align-items: center;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 1.4rem;

    h1 {
      margin: 0;
      font-size: 1.4rem;
      font-weight: normal;
    }
  }

  > div:nth-child(3) {
    margin-right: 20px;
    > a {
      color: ${(p) => p.theme.color.primary.base};
    }
  }
`;

const Title = styled.h1`
  color: #36501c;
  font-family: 'Fibon Neue';
  margin-bottom: 30px;
  text-align: center;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  justify-content: center;

  @media ${device.mobile} {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  label {
    font-weight: bold;
    color: #36501c;
    white-space: nowrap;

    @media ${device.mobile} {
      align-self: flex-start;
    }
  }

  select {
    padding: 8px 15px;
    border: 2px solid #90b361;
    border-radius: 4px;
    font-size: 16px;
    background-color: white;
    color: #36501c;
    cursor: pointer;
    min-width: 150px;

    &:focus {
      outline: none;
      border-color: #36501c;
    }

    @media ${device.mobile} {
      min-width: 100%;
    }
  }
`;

const SearchInput = styled.input`
  padding: 8px 15px;
  border: 2px solid #90b361;
  border-radius: 4px;
  font-size: 16px;
  min-width: 250px;

  &:focus {
    outline: none;
    border-color: #36501c;
  }

  @media ${device.mobile} {
    min-width: 100%;
  }
`;

const ResultCount = styled.div`
  text-align: center;
  margin-bottom: 15px;
  color: #666;
  font-style: italic;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media ${device.mobile} {
    margin-left: -30px;
    margin-right: -30px;
    padding: 0 15px;
  }
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    white-space: nowrap;
  }

  th {
    background-color: #90b361;
    color: #36501c;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  /* Make club column narrower */
  td:nth-child(3), th:nth-child(3) {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Center align numeric columns */
  td:nth-child(1), td:nth-child(4), td:nth-child(5), td:nth-child(6),
  td:nth-child(7), td:nth-child(8), td:nth-child(9), td:nth-child(10), td:nth-child(11),
  th:nth-child(1), th:nth-child(4), th:nth-child(5), th:nth-child(6),
  th:nth-child(7), th:nth-child(8), th:nth-child(9), th:nth-child(10), th:nth-child(11) {
    text-align: center;
  }

  @media ${device.mobile} {
    font-size: 11px;
    min-width: 700px;

    th, td {
      padding: 6px 3px;
    }

    td:nth-child(3), th:nth-child(3) {
      max-width: 100px;
    }
  }
`;

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #7a9851;
  }
`;

const NoData = styled.div`
  text-align: center;
  padding: 50px;
  color: #666;
  font-size: 1.2rem;
`;

const NavLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
`;

const NavLink = styled(RouterNavLink)`
  padding: 10px 20px;
  border: 2px solid #90b361;
  border-radius: 4px;
  text-decoration: none;
  color: #36501c;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background-color: #90b361;
    color: white;
  }

  &.active {
    background-color: #36501c;
    border-color: #36501c;
    color: white;
  }
`;

const RankCell = styled.td`
  font-weight: bold;
  color: #666;
`;

const YearDropdownContainer = styled.div`
  position: relative;
  min-width: 200px;

  @media ${device.mobile} {
    min-width: 100%;
  }
`;

const YearDropdownButton = styled.button`
  width: 100%;
  padding: 8px 15px;
  border: 2px solid #90b361;
  border-radius: 4px;
  font-size: 16px;
  background-color: white;
  color: #36501c;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;

  &:hover {
    background-color: #f9f9f9;
  }

  &:focus {
    outline: none;
    border-color: #36501c;
  }
`;

const DropdownArrow = styled.span`
  margin-left: 10px;
  font-size: 12px;
  color: #36501c;
`;

const YearDropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: white;
  border: 2px solid #90b361;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
`;

const YearCheckboxItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f7e9;
  }

  label {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin: 0;
    font-weight: normal;
    white-space: nowrap;

    input[type="checkbox"] {
      margin-right: 10px;
      cursor: pointer;
      width: 18px;
      height: 18px;
      accent-color: #90b361;
    }

    span {
      flex: 1;
    }
  }
`;
